'use client';

import { CSSProperties, FC, useEffect } from 'react';
import type { ComponentType } from 'react';
import classNames from 'classnames';
import { ColorField, ImageField } from '@prismicio/client';

import { Heading, Icon, Link, Slider, useData } from '@/components';
import {
  useProductsByCategory,
  useSelectedStore,
  useShowByLocationSlug,
} from '@/hooks';
import { useAnalytics } from '@/data';
import { getSlicePadding } from '@/utils';
import {
  EnumCustomerType,
  GProduct,
  SortBySelectOption,
  TreezStore,
} from '@/types';
import { TEMPLATE_BASE_PATH } from '@/constants';

import ProductPlaceholder from '../Product/ProductPlaceholder';
import Product from '../Product/Product';
import { useKioskBasePath } from '../../_hooks';
import { buildKioskCarouselBreakpoints } from '../../_utils/kioskProductsLayout';

import style from './products.module.scss';

// Kiosk TS language service intermittently drops SwiperProps from Slider; widen for this call site.
const ProductsCarouselSlider = Slider as ComponentType<any>;

interface IProductsProps {
  title: string;
  analyticsListName?: string;
  products: GProduct[];
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
  totalCount?: number;
  seeAllHref?: string;
  seeMoreText?: string;

  // limit and sort
  limit?: number;
  sortBy?: SortBySelectOption | undefined;

  // filters
  filterByStore?: boolean;
  filterByCategory?: string; // filter by category
  filterByBrand?: string; // filter by brand
  filterByGroupId?: string; // filter by group id
  filterByOnSale?: boolean; // filter by on sale
  locationSlug?: string;
  effects?: string;
  flavors?: string;
  generals?: string;

  backgroundColor?: ColorField;
  backgroundImage?: ImageField;
  backgroundStyleFixed?: boolean;
  minHeight?: number;

  titleColor?: ColorField;
  seeAllTextColor?: ColorField;
  activeControlColor?: ColorField;
  disableControlColor?: ColorField;

  spacingBottom?: boolean;
  paddingSection?: 'small' | 'medium' | 'large';
}

const Products: FC<IProductsProps> = ({
  title,
  analyticsListName,
  totalCount: _totalCount = 0,
  seeAllHref,
  products: serverProducts,
  selectedStore,
  selectedCustomerType,
  seeMoreText = 'See All',

  limit = 20,
  sortBy,

  filterByStore = false,
  filterByCategory,
  filterByBrand,
  filterByGroupId,
  filterByOnSale,
  locationSlug,
  effects,
  flavors,
  generals,

  backgroundColor,
  backgroundImage,
  backgroundStyleFixed,
  minHeight,

  titleColor,
  seeAllTextColor,
  activeControlColor,
  disableControlColor,

  spacingBottom: bottom_spacing = false,
  paddingSection = 'small',
}) => {
  const { measureProductImpressions, measureViewItemsListGA4 } = useAnalytics();
  const { productCard } = useData();
  const { isKioskLargeCard, spacingBetweenCard } = productCard.displayProps;
  const carouselBreakpoints = buildKioskCarouselBreakpoints({
    isKioskLargeCard,
    spacingBetweenCard,
    spaceBetween: 16,
  });

  const { store } = useSelectedStore();
  const { showContent } = useShowByLocationSlug(locationSlug);
  const { basePath } = useKioskBasePath();
  const sanitizedSeeAllHref = seeAllHref?.startsWith('/')
    ? seeAllHref
    : `/${seeAllHref}`;
  const kioskSeeAllHref = `${basePath}${TEMPLATE_BASE_PATH.SHOP}${sanitizedSeeAllHref}`;

  const { products, isLoading, total, isError, error } = useProductsByCategory({
    store: store ?? selectedStore,
    customerType: selectedCustomerType,
    sortBy,
    limit,
    products: serverProducts,
    filterByStore,
    filterByBrand,
    filterByCategory,
    filterByGroupId,
    filterByOnSale,
    locationSlug,
    effects,
    flavors,
    generals,
  });

  useEffect(() => {
    measureProductImpressions(products, analyticsListName as string);
    measureViewItemsListGA4(products, analyticsListName as string);
  }, []);

  const background = seeAllTextColor ? 'transparent' : 'initial';

  const ariaLabel = title ? `${title} carousel` : 'Products carousel';

  if (isError) {
    // eslint-disable-next-line no-console
    console.log(error);

    return null;
  }

  if ((!isLoading && !products?.length) || !showContent) {
    return null;
  }

  const carouselStyle = {
    ...(activeControlColor
      ? { '--products-control-color': activeControlColor }
      : {}),
    ...(disableControlColor
      ? { '--products-control-disabled-color': disableControlColor }
      : {}),
    ...(seeAllTextColor ? { '--products-control-border': 'transparent' } : {}),
  } as CSSProperties;

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(
        style.product__section,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: bottom_spacing,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage?.url
          ? `url(${backgroundImage?.url})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
        ...carouselStyle,
      }}
    >
      <ProductsCarouselSlider
        activatedHover
        ariaLabel={ariaLabel}
        breakpoints={carouselBreakpoints}
        centeredSlides={false}
        className={classNames('products__carousel', {
          products__carousel_card_large: isKioskLargeCard,
        })}
        freeMode
        key={store?.short_name}
        seeAllElement={
          <Link
            aria-label={`Go to ${kioskSeeAllHref ?? '/deals'}`}
            className={classNames(style.products__see_all)}
            href={kioskSeeAllHref ?? '/deals'}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText ?? 'SHOP ALL'} {total > 0 && <span>({total})</span>}
          </Link>
        }
        showSlidesOutsideContainer
        sliderHeading={
          <Heading level={2} style={{ color: titleColor! }}>
            {title}
          </Heading>
        }
        slidesPerView="auto"
        spaceBetween={16}
      >
        {isLoading
          ? Array.from({ length: 20 }).map((_, index) => (
              <ProductPlaceholder key={index} />
            ))
          : products.map(product => (
              <Product
                analyticsListName={analyticsListName}
                className={style.slide__main}
                key={product.id}
                product={product}
              />
            ))}
        <Link
          aria-label={`Go to ${kioskSeeAllHref ?? '/deals'}`}
          className={classNames(
            style.slide__main,
            style.product__shop_all_slider,
          )}
          href={kioskSeeAllHref ?? '/deals'}
          style={{
            color: seeAllTextColor!,
            backgroundColor: background,
          }}
        >
          <Icon name="right" />
          <p
            style={{
              color: seeAllTextColor!,
            }}
          >
            {seeMoreText}
          </p>
        </Link>
      </ProductsCarouselSlider>
    </section>
  );
};

export { Products };
